import React, { useState, useCallback } from 'react';
import { Upload, File, X, Download, Eye, Trash2 } from 'lucide-react';
import { Button } from '../../../ui-components/ui/button';
import { Card } from '../../../ui-components/ui/card';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { useUser } from '../../../lib/hooks/use-user';

interface FileItem {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  uploadedBy: string;
  url?: string;
}

interface FileUploadProps {
  dealId: string;
  category: 'files' | 'documents';
  onFileChange?: () => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ dealId, category, onFileChange }) => {
  const { user } = useUser();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Load files from localStorage on mount
  React.useEffect(() => {
    const storedFiles = localStorage.getItem(`deal_${category}_${dealId}`);
    if (storedFiles) {
      setFiles(JSON.parse(storedFiles));
    }
  }, [dealId, category]);

  const saveFiles = (newFiles: FileItem[]) => {
    localStorage.setItem(`deal_${category}_${dealId}`, JSON.stringify(newFiles));
    setFiles(newFiles);
    onFileChange?.();
  };

  const addToHistory = (action: string, fileName: string) => {
    const historyEntry = {
      _id: `local_${Date.now()}`,
      dealId: dealId,
      belongsToUserId: user?._id || 'unknown',
      historyType: 'FILE_ACTIVITY' as any,
      historyNoteContent: {
        _id: `note_${Date.now()}`,
        description: `${action} ${category === 'documents' ? 'Dokument' : 'Datei'}: ${fileName}`
      },
      createdAt: new Date().toISOString(),
      belongsToUserFullName: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer',
    };

    const existingHistory = JSON.parse(localStorage.getItem(`deal_history_${dealId}`) || '[]');
    localStorage.setItem(`deal_history_${dealId}`, JSON.stringify([historyEntry, ...existingHistory]));
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  };

  const handleFiles = async (fileList: File[]) => {
    setIsUploading(true);
    
    const newFiles: FileItem[] = fileList.map(file => ({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      uploadedBy: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer',
      // In a real app, you'd upload to a server and get a URL back
      // For now, we'll create a local object URL (temporary)
      url: URL.createObjectURL(file)
    }));

    const updatedFiles = [...files, ...newFiles];
    saveFiles(updatedFiles);
    
    // Add to history
    newFiles.forEach(file => {
      addToHistory('Hochgeladen', file.name);
    });
    
    setIsUploading(false);
    toast.success(`${fileList.length} Datei${fileList.length > 1 ? 'en' : ''} erfolgreich hochgeladen`);
  };

  const handleDelete = (fileId: string) => {
    const fileToDelete = files.find(f => f.id === fileId);
    const updatedFiles = files.filter(f => f.id !== fileId);
    saveFiles(updatedFiles);
    
    // Add to history
    if (fileToDelete) {
      addToHistory('Gelöscht', fileToDelete.name);
    }
    
    toast.success('Datei gelöscht');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('sheet') || type.includes('excel')) return '📊';
    if (type.includes('presentation') || type.includes('powerpoint')) return '📽️';
    return '📎';
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 mb-4">
          {category === 'documents' 
            ? 'Dokumente hochladen oder hierher ziehen'
            : 'Dateien hochladen oder hierher ziehen'}
        </p>
        <input
          type="file"
          id={`file-upload-${category}`}
          className="hidden"
          multiple
          onChange={handleFileSelect}
          accept={category === 'documents' ? '.pdf,.doc,.docx' : '*'}
        />
        <label htmlFor={`file-upload-${category}`}>
          <Button variant="outline" asChild disabled={isUploading}>
            <span className="cursor-pointer">
              {isUploading ? 'Wird hochgeladen...' : 'Dateien auswählen'}
            </span>
          </Button>
        </label>
        <p className="text-sm text-gray-500 mt-2">
          {category === 'documents' 
            ? 'Nur PDF und Word-Dokumente erlaubt'
            : 'Alle Dateitypen erlaubt'}
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium text-gray-900">
            {category === 'documents' ? 'Hochgeladene Dokumente' : 'Hochgeladene Dateien'}
          </h3>
          {files.map((file) => (
            <Card key={file.id} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <span className="text-2xl">{getFileIcon(file.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(file.size)} • Hochgeladen {formatDistanceToNow(new Date(file.uploadedAt), { addSuffix: true, locale: de })} von {file.uploadedBy}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {file.url && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(file.url, '_blank')}
                        title="Anzeigen"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = file.url!;
                          a.download = file.name;
                          a.click();
                        }}
                        title="Herunterladen"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(file.id)}
                    title="Löschen"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {files.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          <File className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">
            {category === 'documents' 
              ? 'Noch keine Dokumente hochgeladen'
              : 'Noch keine Dateien hochgeladen'}
          </p>
        </div>
      )}
    </div>
  );
};
